import { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import AuthControl from "@/components/auth/AuthControl";
import BrandMark from "@/components/BrandMark";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { HeaderNav, MenuNav } from "@/components/navigation/SiteNav";
import { useNavFit } from "@/hooks/useNavFit";

// L'en-tête de l'accueil : fixe, translucide sur le héros. Sa navigation est
// celle de toutes les pages (RDEC-019), et sa barre a la même géométrie que
// celle des pages internes (conteneur, logo) : plus étroite, elle ne tenait pas
// à 1440 px en français. Seul l'habillage lui est propre.
export const LandingHeader = () => {
  const [open, setOpen] = useState(false);
  const { barRef, brandRef, navRef, controlsRef, toggleRef, navFits } = useNavFit();

  // Le menu se referme si la barre retrouve la place de tout afficher.
  useEffect(() => {
    if (navFits) setOpen(false);
  }, [navFits]);

  // Lock body scroll while the mobile menu is open.
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <header
      data-testid="site-header"
      className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-paper/80 border-b border-ink/10"
    >
      <div
        ref={barRef}
        className="max-w-[1600px] mx-auto px-5 md:px-12 h-20 md:h-24 flex items-center justify-between gap-3"
      >
        <a href="#top" ref={brandRef} data-testid="header-brand" className="flex items-center shrink-0">
          <BrandMark />
        </a>

        <HeaderNav ref={navRef} fits={navFits} />

        <div ref={controlsRef} className="flex items-center gap-3 sm:gap-4 shrink-0">
          <div className="hidden sm:block">
            <AuthControl />
          </div>
          <LanguageSwitcher compact />
          <button
            type="button"
            ref={toggleRef}
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
            aria-expanded={open}
            data-testid="mobile-menu-toggle"
            className={`${navFits ? "hidden" : "flex"} items-center justify-center h-11 w-11 -mr-2 text-gold-ink`}
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

        {/* Le corps ne défile plus tant que le menu est ouvert. Sans défilement
            propre au menu, tout ce qui dépasse l'écran devenait inatteignable —
            et la déconnexion, dernière entrée de la liste, tombait sous la ligne
            de flottaison sur un téléphone. `dvh` plutôt que `vh` : seule cette
            unité tient compte de la barre d'adresse mobile, qui est précisément
            ce qui manquait. */}
      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="border-t border-ink/10 bg-paper/95 backdrop-blur-xl max-h-[calc(100dvh-5rem)] overflow-y-auto overscroll-contain"
          >
            <div className="max-w-7xl mx-auto px-5 py-4 flex flex-col">
              <MenuNav onNavigate={() => setOpen(false)} />
              <div className="mt-4 pt-4 border-t border-ink/10 sm:hidden">
                <AuthControl variant="stack" onNavigate={() => setOpen(false)} />
              </div>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export default LandingHeader;
